/**
 * Which hint a field shows (R15, R16). With hints switched on, the chosen
 * option's hint (doctor, facility, lead source category, lead source) takes
 * the field's hint slot; otherwise the field's own hint shows. Coordinator and
 * plan type have no per-option hints, so they always show the field hint.
 * Hints are plain text (KTD14); callers render them as text, never as HTML.
 */
import type { FormState, NewPatientFormSettings, OptionHintList, ResolvedField } from './newPatientFormRules';

/** Field key → the option list whose hints apply and the chosen id. */
const OPTION_HINT_SOURCES: ReadonlyArray<{ key: string; list: OptionHintList; pick: (s: FormState) => number | null }> = [
  { key: 'doctor', list: 'doctors', pick: (s) => s.doctorId },
  { key: 'facility', list: 'facilities', pick: (s) => s.facilityId },
  { key: 'referral_select_1_anchor', list: 'referral_level1', pick: (s) => s.referralCategoryId },
  { key: 'referral_name', list: 'referral_level2', pick: (s) => s.referralSourceId },
];

/** The chosen option's hint per field key, for fields with a non-blank one. */
export function selectedOptionHints(
  optionHints: NewPatientFormSettings['optionHints'] | undefined,
  state: FormState,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!optionHints) return out;
  for (const source of OPTION_HINT_SOURCES) {
    const id = source.pick(state);
    if (id === null || id <= 0) continue;
    const text = optionHints[source.list]?.[String(id)];
    if (typeof text === 'string' && text.trim() !== '') out[source.key] = text.trim();
  }
  return out;
}

/** The text for a field's hint slot, or null when nothing shows. */
export function hintText(field: ResolvedField, hintsEnabled: boolean, optionHint?: string | null): string | null {
  if (!hintsEnabled) return null;
  const option = optionHint?.trim();
  if (option) return option;
  const own = field.hint?.trim();
  return own ? own : null;
}
